import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, HelpCircle, ImageOff, Loader2, Maximize2, ScanSearch, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pill } from '@/components/ui/pill';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useApi } from '@/hooks/useApi';
import { useReviewScreenshot } from '@/hooks/useScreenshotAudit';
import { useInvalidateMoney } from '@/hooks/useInvalidateMoney';
import { verdictLabel, verdictTone } from '@/lib/labels';
import { formatDate } from '@/lib/dates';
import { POSITIVE_REPLY } from '@/lib/outreach-copy';
import { VerdictButton } from './VerdictButton';
import type { OutreachInvite, ScreenshotVerdict } from '@/api/types';

/**
 * Proof review for one invite, opened from its detail sheet on the admin
 * Invites page: the proof screenshot, any verdict already recorded, and the
 * same Proof OK / Proof fails / Can't tell actions as the Proof review queue,
 * so a reviewer who spots something while browsing invites doesn't have to go
 * and find it in the queue.
 */
export function InviteScreenshotAudit({ invite }: { invite: OutreachInvite }): JSX.Element {
  const [open, setOpen] = useState(false);
  const hasScreenshot = !!invite.screenshotKey;
  const verdict = invite.screenshotVerdict ?? null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={!hasScreenshot}
        title={hasScreenshot ? 'See the proof screenshot and record a verdict' : 'No screenshot was uploaded for this invite'}
        onClick={() => setOpen(true)}
      >
        <ScanSearch className="mr-1.5 h-3.5 w-3.5" />
        {hasScreenshot ? 'Review proof' : 'No screenshot'}
        {verdict && (
          <Pill tone={verdictTone(verdict)} icon={false} className="ml-2">
            {verdictLabel(verdict)}
          </Pill>
        )}
      </Button>
      {open && <AuditDialog invite={invite} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * The proof review dialog on its own, for opening from a menu item (the
 * Invites row kebab and the sheet's More menu) instead of the button above.
 */
export function InviteScreenshotAuditDialog({
  invite,
  onClose,
}: {
  invite: OutreachInvite;
  onClose: () => void;
}): JSX.Element {
  return <AuditDialog invite={invite} onClose={onClose} />;
}

/**
 * The proof screenshot's URL. Presigned and short-lived, so fetched when it
 * is shown rather than with the invites list; the dialog and the inline
 * panel share the cache entry.
 */
function useInviteScreenshotUrl(invite: OutreachInvite) {
  const api = useApi();
  return useQuery({
    queryKey: ['invite-screenshot', 'admin', invite.publicId],
    queryFn: () =>
      api.get<{ url: string }>(`/admin/invites/${invite.publicId}/screenshot`).then((v) => v.url),
    enabled: !!invite.screenshotKey,
    staleTime: 5 * 60_000,
  });
}

/** Record a verdict for one invite's proof (shared by the dialog and the inline panel). */
function useSubmitVerdict(invite: OutreachInvite, onDone?: () => void) {
  const invalidateMoney = useInvalidateMoney();
  const review = useReviewScreenshot();
  const submit = (verdict: ScreenshotVerdict, note: string): void => {
    review.mutate(
      { invitePublicId: invite.publicId, verdict, note: note.trim() || null },
      {
        onSuccess: () => {
          toast.success(`@${invite.targetIdentifier}: ${verdictLabel(verdict)}`);
          // The verdict lives on the invite row, so the table's copy is stale.
          invalidateMoney();
          onDone?.();
        },
        onError: (e: unknown) => toast.error((e as Error).message),
      },
    );
  };
  return { submit, pending: review.isPending };
}

/**
 * Proof shown inline at the top of an invite's detail sheet: the screenshot
 * (click to open it full size in the review dialog), the current verdict,
 * and the three verdict buttons. Renders nothing when no proof was uploaded.
 */
export function InviteProofInline({ invite }: { invite: OutreachInvite }): JSX.Element | null {
  const image = useInviteScreenshotUrl(invite);
  const { submit, pending } = useSubmitVerdict(invite);
  const [note, setNote] = useState(invite.screenshotReviewNote ?? '');
  const [expanded, setExpanded] = useState(false);
  if (!invite.screenshotKey) return null;
  const verdict = invite.screenshotVerdict ?? null;

  return (
    <section aria-label="Proof screenshot" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Proof</h3>
        {verdict ? (
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <Pill tone={verdictTone(verdict)} icon={false}>{verdictLabel(verdict)}</Pill>
            {invite.screenshotReviewedAt ? formatDate(invite.screenshotReviewedAt) : ''}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Not reviewed yet</span>
        )}
      </div>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        disabled={!image.data}
        className="group relative flex min-h-[160px] w-full items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/30 p-1.5"
        title={image.data ? 'Open full size' : undefined}
      >
        {image.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : image.data ? (
          <>
            <img
              src={image.data}
              alt={`Screenshot for @${invite.targetIdentifier}`}
              className="max-h-[360px] w-auto max-w-full rounded object-contain"
            />
            <span className="absolute right-2 top-2 rounded-md bg-background/80 p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
              <Maximize2 className="h-3.5 w-3.5" />
            </span>
          </>
        ) : (
          <span className="flex flex-col items-center gap-1.5 text-xs text-muted-foreground">
            <ImageOff className="h-6 w-6" />
            {(image.error as Error | null)?.message ?? "Couldn't load this image."}
          </span>
        )}
      </button>
      {invite.screenshotReviewNote && verdict && (
        <p className="text-xs text-foreground/85">"{invite.screenshotReviewNote}"</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="h-8 min-w-0 flex-1 text-xs"
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          aria-label="Review note"
        />
        <div className="grid grid-cols-3 gap-1.5">
          <VerdictButton
            onClick={() => submit('APPROVED', note)}
            disabled={pending}
            icon={<Check className="h-4 w-4" />}
            label="Proof OK"
            tone="approve"
          />
          <VerdictButton
            onClick={() => submit('REJECTED', note)}
            disabled={pending}
            icon={<X className="h-4 w-4" />}
            label="Proof fails"
            tone="reject"
          />
          <VerdictButton
            onClick={() => submit('UNCLEAR', note)}
            disabled={pending}
            icon={<HelpCircle className="h-4 w-4" />}
            label="Can't tell"
            tone="unclear"
          />
        </div>
      </div>
      {expanded && <AuditDialog invite={invite} onClose={() => setExpanded(false)} />}
    </section>
  );
}

function AuditDialog({ invite, onClose }: { invite: OutreachInvite; onClose: () => void }): JSX.Element {
  const [note, setNote] = useState(invite.screenshotReviewNote ?? '');
  const image = useInviteScreenshotUrl(invite);
  const { submit: submitVerdict, pending } = useSubmitVerdict(invite, onClose);
  const submit = (verdict: ScreenshotVerdict): void => submitVerdict(verdict, note);

  const verdict = invite.screenshotVerdict ?? null;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Proof for @{invite.targetIdentifier}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_260px]">
          <div className="flex min-h-[320px] items-center justify-center rounded-md border border-border bg-muted/30 p-2">
            {image.isLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : image.data ? (
              <img
                src={image.data}
                alt={`Screenshot for @${invite.targetIdentifier}`}
                className="max-h-[70vh] w-auto max-w-full rounded object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                <ImageOff className="h-8 w-8" />
                <p>{(image.error as Error | null)?.message ?? "Couldn't load this image."}</p>
              </div>
            )}
          </div>

          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-warning/40 bg-warning/[0.08] px-3 py-2 text-xs leading-relaxed">
              <span className="font-bold text-foreground">{POSITIVE_REPLY.headline}</span>{' '}
              <span className="text-foreground/85">{POSITIVE_REPLY.qualifies}</span>{' '}
              <span className="text-foreground/85">{POSITIVE_REPLY.doesNot}</span>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current verdict</div>
              {verdict ? (
                <div>
                  <Pill tone={verdictTone(verdict)} icon={false}>{verdictLabel(verdict)}</Pill>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {invite.screenshotReviewedAt ? `Reviewed ${formatDate(invite.screenshotReviewedAt)}` : ''}
                  </span>
                  {invite.screenshotReviewNote && (
                    <p className="mt-1 text-xs text-foreground/85">"{invite.screenshotReviewNote}"</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Not reviewed yet.</p>
              )}
            </div>

            <Input
              placeholder="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
            />
            <div className="grid grid-cols-3 gap-2">
              <VerdictButton
                onClick={() => submit('APPROVED')}
                disabled={pending}
                icon={<Check className="h-4 w-4" />}
                label="Proof OK"
                tone="approve"
              />
              <VerdictButton
                onClick={() => submit('REJECTED')}
                disabled={pending}
                icon={<X className="h-4 w-4" />}
                label="Proof fails"
                tone="reject"
              />
              <VerdictButton
                onClick={() => submit('UNCLEAR')}
                disabled={pending}
                icon={<HelpCircle className="h-4 w-4" />}
                label="Can't tell"
                tone="unclear"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Records a verdict only. It doesn't write off the invite or change what anyone is
              owed; use Reject invite for that.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
